/**
 * Todas as strings de interface, em pt-BR.
 *
 * Centralizadas desde a primeira tela, e o §10 da spec 02 diz por quê — não é por internacionalização
 * futura: *"string espalhada é o que impede revisar tom e termo jurídico de uma vez"*. Neste produto
 * há frases que falam de **retenção de dado pessoal** e de **acesso a conversa de terceiro**; essas
 * precisam ser revisáveis num arquivo, não caçadas em vinte componentes.
 *
 * **Vocabulário fixo** (§10), e não alternar: "canal", "conexão", "conversa", "atendente",
 * "template". Nunca "instância", "sessão", "chat", "operador", "modelo".
 *
 * E "atendente" é gente. **"Agente" é o agente de LLM** — usar a mesma palavra para os dois é bug de
 * vocabulário esperando acontecer (§3 da spec 05).
 */

const formatarData = new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" });

export const textos = {
  acoes: {
    tentarDeNovo: "Tentar de novo",
    recarregar: "Recarregar",
    reconectar: "Reconectar",
    entrar: "Entrar",
    detalheTecnico: "Detalhe técnico",
    voltar: "Voltar",
  },

  estados: {
    carregando: "Carregando…",

    erro: {
      titulo: "Algo deu errado",
      corpo: "Não conseguimos concluir. Tente de novo; se continuar, mande o detalhe técnico ao suporte.",
    },

    indisponivel: {
      // NÃO diz "faça login de novo". Esta tela aparece quando o problema é NOSSO — 503 ou rede — e
      // mandar a pessoa para o login a levaria a um login que depende do mesmo serviço que caiu.
      titulo: "Não conseguimos falar com o servidor",
      espere: (segundos: number) =>
        segundos <= 1
          ? "Tente de novo em instantes."
          : `Tente de novo em cerca de ${segundos} segundos.`,
    },

    /**
     * O estado PADRÃO da tela de entrada — e ele não sabe o que aconteceu antes.
     *
     * ⚠️ Até 18/09 o título aqui era **"Sua sessão terminou"**, e isso era uma afirmação que o
     * app não tem como sustentar. Este estado cobre TRÊS situações indistinguíveis entre si:
     * quem nunca entrou, quem teve a sessão vencida, e quem limpou os cookies. A única que o
     * Hugi sabe reconhecer é a saída deliberada (`marcarSaida`) — não há marca de expiração.
     *
     * Para quem chega pela primeira vez, "sua sessão terminou" é uma frase sobre um evento que
     * não houve. O owner encontrou assim: abriu a tela e leu que a sessão dele tinha terminado
     * sem nunca ter começado.
     *
     * O raciocínio já estava escrito logo abaixo, em `saiu` — frase passiva depois de ação
     * deliberada sugere problema onde não houve. Ele vale igual aqui, e ninguém o aplicou.
     *
     * Um texto neutro é correto nos três casos. Se um dia existir marca de expiração, aí sim
     * cabe um estado próprio dizendo que a sessão venceu — e aí será verdade.
     */
    semSessao: {
      titulo: "Entrar no Hugi",
      corpo: "Use a sua conta Cert4All para continuar.",
    },

    /**
     * Para quem clicou em **Sair**, e não para quem foi deslogado.
     *
     * "Sua sessão terminou" descreve algo que aconteceu **com** a pessoa; sair é algo que ela
     * **fez**. Ler a frase passiva depois de uma ação deliberada sugere problema onde não houve
     * nenhum — e a diferença importa mais aqui do que parece, porque a mesma tela também é a que
     * aparece quando a sessão vence de verdade.
     */
    saiu: {
      titulo: "Você saiu",
      corpo: "Entre de novo quando quiser continuar.",
    },

    /**
     * Sessão VÁLIDA, e a pessoa não é da plataforma.
     *
     * O texto não oferece "entrar de novo" de propósito: entrar não muda nada, e a versão anterior — que
     * reusava `semSessao` — mandava a pessoa para um laço que nunca resolve.
     */
    semPlataforma: {
      titulo: "Este endereço é do time do Hugi",
      corpo:
        "Sua conta está certa, mas este console é da equipe que opera a plataforma. O seu atendimento fica no painel do cliente.",
    },

    semPermissao: {
      titulo: "Você não tem acesso a esta tela",
      qual: (permissao: string) => `Ela exige a permissão ${permissao}.`,
      semNome: "Ela exige uma permissão que a sua conta não tem.",
      // Nomear quem concede é o que transforma um chamado de suporte numa conversa de trinta
      // segundos dentro da própria empresa.
      quemConcede: "Quem pode conceder é um administrador da sua empresa, em Equipe e acessos.",
    },

    /*
      O título NOMEIA a empresa. Em 30/09 o owner entrou por uma empresa que não era a dele (cancelada em
      13/08) e a tela só dizia "esta empresa" e "fale com o comercial" — ele concluiu que o login estava
      quebrado. Quem cai aqui pela conta errada precisa ler QUAL conta, e a saída é trocar de conta, não
      comprar. O Cert4All não diz se a assinatura foi cancelada ou nunca existiu: "não tem" vale para as duas.
    */
    semEntitlement: {
      titulo: (nome: string | null) => (nome ? `${nome} não tem o Hugi ativo` : "Esta empresa não tem o Hugi ativo"),
      corpo: "Se não é a sua empresa, entre com outra conta; se é, fale com o comercial.",
      trocarDeConta: "Entrar com outra conta",
      saindo: "Saindo…",
    },

    vazioPorEscolha: {
      titulo: "Suas conversas começam agora",
      // A invariante 6 do §7: nunca renderizar histórico que não temos, e DIZER por quê. Sem esta
      // frase, o cliente conecta, vê vazio e conclui que não funcionou.
      corpo:
        "Não trazemos o histórico anterior à conexão: ele fica no aplicativo do WhatsApp, e preferimos " +
        "não copiar conversa que você não nos pediu para guardar. Daqui para frente, tudo que chegar " +
        "aparece aqui.",
    },

    semTexto: "Mensagem sem texto",

    // A invariante 3 do §7. A data e o remetente da mensagem continuam na tela — é exatamente o que
    // o expurgo preserva de propósito (ADR-0003).
    conteudoRemovido: (removidoEm: Date, diasDeRetencao: number | null) =>
      diasDeRetencao === null
        ? `Conteúdo removido em ${formatarData.format(removidoEm)} pela política de retenção.`
        : `Conteúdo removido em ${formatarData.format(removidoEm)} pela política de retenção de ${diasDeRetencao} dias.`,

    canalRevogado: (numero: string | null) =>
      numero
        ? `O número ${numero} foi desconectado. Nada sai por este canal até reconectar.`
        : "Este canal foi desconectado. Nada sai por ele até reconectar.",

    versaoIncompativel: {
      titulo: "Esta página está desatualizada",
      corpo: "O servidor foi atualizado enquanto você estava aqui. Recarregue para continuar.",
    },

    iamNaoConfigurado: {
      titulo: "Este servidor está sem autenticação configurada",
      corpo:
        "Falta CERT4ALL_API_URL e CERT4ALL_API_KEY no ambiente do servidor. Nenhuma tela " +
        "administrativa deve subir contra este estado — nem em desenvolvimento.",
    },
  },

  /** Rótulos de estado de canal. Sempre acompanham a cor; nunca a substituem (§10). */
  canal: {
    conectado: "Conectado",
    // "Aguardando pareamento" e não "Desconectado": existe canal, falta um passo humano. Chamar de
    // desconectado faria o cliente procurar defeito onde falta uma ação dele.
    pareando: "Aguardando leitura do QR",
    desconectado: "Desconectado",
    degradado: "Conectado, com qualidade reduzida",
  },

  console: {
    recusa: {
      titulo: "Este endereço não é para a sua conta",
      corpo: "O console é da equipe do Hugi. Se você é cliente, use o painel.",
    },
  },

  app: {
    painel: "Hugi — painel",
    inbox: "Hugi — atendimento",
    console: "Hugi — console",
  },
} as const;
